import React from "react";

interface Props {
  children: React.ReactNode;
  /** When this value changes (e.g. the route), the boundary auto-resets. */
  resetKey?: string;
  /** Optional compact fallback (e.g. for an inline widget like the wallet
   *  button) rendered instead of the full-page "SIGNAL LOST" card. */
  fallback?: React.ReactNode;
}
interface State {
  hasError: boolean;
  key?: string;
}

/**
 * App-wide error boundary. Any render error (e.g. an undefined component thrown
 * while showing a failed blockchain transaction) is caught here and replaced
 * with a calm, on-brand fallback — the raw React error / crash overlay is never
 * shown to end users, and the rest of the app (nav, routing) keeps working.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, key: this.props.resetKey };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  // Reset automatically when the route (resetKey) changes, so navigating away
  // from a broken view clears the error.
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.key) return { hasError: false, key: props.resetKey };
    return null;
  }

  componentDidCatch(error: unknown, info: unknown) {
    // Log for diagnostics only — never surfaced to the user.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info);
  }

  private reset = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return (
      <div className="err-boundary">
        <div className="err-card panel ticks">
          <div className="err-eyebrow mono"><span className="dot" />SIGNAL LOST</div>
          <h2 className="err-title">Something went wrong</h2>
          <p className="err-text">
            An unexpected error interrupted this view. Your wallet, assets and funds are safe — nothing on-chain was affected.
          </p>
          <div className="err-actions">
            <button className="btn btn-primary" onClick={this.reset} type="button">
              TRY AGAIN <span className="ar">→</span>
            </button>
            <a className="btn btn-ghost" href="/">BACK TO HOME</a>
          </div>
        </div>
      </div>
    );
  }
}
